import React, { useEffect, useState } from 'react';
import { CFormLabel } from '@coreui/react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useAuth } from '../contexts/AuthContext';
import { useReport } from '../contexts/ReportContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { formatDateToBackend } from '../components/Helper';
import { reportNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import BarChart from '../components/BarChart';
import PageHeader from '../components/PageHeader';
import DateRangeSelection from '../components/DateRangeSelection';
import ViewSingleReceiptReport from '../components/ViewSingleReceiptReport';

function TableReport() {
    const { tableReportCharts, tableReport } = useReport();
    const { currency } = useCompanySetting();
    const { restaurantId } = useAuth();

    const currentDate = new Date();
    const yesterdayDate = new Date(currentDate);
    yesterdayDate.setDate(currentDate.getDate() - 1);

    const [dates, setDates] = useState([yesterdayDate, currentDate]);
    const [receiptId, setReceiptId] = useState(null);
    const [reportList, setReportList] = useState([]);
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedProductRows, setExpandedProductRows] = useState([]);
    const [paymentChartData, setPaymentChartData] = useState({
        x: [],
        y: []
    });
    const [tableReserve, setTableReserveData] = useState({
        x: [],
        y: []
    });

    const startDate = dates.length > 0 && dates[0] ? formatDateToBackend(dates[0]) : null;
    const endDate = dates.length > 0 && dates[1] ? formatDateToBackend(dates[1]) : null;

    const fetchTableReport = async () => {
        try {
            setIsLoading(true);
            setReportList([]);
            setTotalRecords(0);

            const reports = await tableReport(currentPage, rowsPerPage, sortField, sortOrder, startDate, endDate);
            if (reports.data.length > 0) {
                setReportList(reports.data);
                setTotalRecords(reports.totalCount);
            }

        } catch (error) {
            console.error('Error fetching table reports', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchTableReport();
        }
    }, [currentPage, rowsPerPage, sortField, sortOrder, restaurantId, startDate, endDate]);

    const fetchTableReportCharts = async () => {
        try {
            setIsLoading(true);
            const reports = await tableReportCharts(startDate, endDate);
            if (reports.data.length > 0) {
                const table = reports.data.map(item => item.tableName);
                const totalHours = reports.data.map(item => item.totalHours);
                const totalRevenue = reports.data.map(item => item.totalRevenue);

                setPaymentChartData({
                    x: table,
                    y: totalRevenue
                })

                setTableReserveData({
                    x: table,
                    y: totalHours
                });
            } else {
                setPaymentChartData({
                    x: [],
                    y: []
                })

                setTableReserveData({
                    x: [],
                    y: []
                });
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching table report charts', error);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchTableReportCharts();
        }
    }, [restaurantId, startDate, endDate]);

    const handleSorting = async (e) => {
        const field = e.sortField;
        const order = e.sortOrder;

        setSortField(field);
        setSortOrder(order);
        fetchTableReport();
    };

    const onPageChange = (event) => {
        const newCurrentPage = Math.floor(event.first / event.rows) + 1;
        setCurrentPage(newCurrentPage);
        const newRowsPerPage = event.rows;
        setRowsPerPage(newRowsPerPage);
    };

    const allowExpansion = (rowData) => {
        return rowData.tableLog?.length > 0;
    };

    const rowExpansionTemplate = (rowData) => {
        const items = rowData.tableLog;

        return (
            <div>
                {items && items.length > 0 && (
                    <DataTable
                        className='table-report-expansion-table'
                        lazy
                        value={items}
                        dataKey="tableId"
                    >
                        <Column style={{ width: '3em' }} />
                        <Column
                            field="tableId"
                            className='receipt_report_table table_id ps-0' />
                        <Column
                            field="tableName"
                            className='receipt_report_table tableName p-0' />
                        <Column
                            field="floorName"
                            className='receipt_report_table floorName p-0' />
                        <Column
                            field="totalHours"
                            className='receipt_report_table totalHours p-0' />
                        <Column
                            field="total"
                            className='receipt_report_table totalRevenue'
                            sortable
                            body={(rowData) => (
                                <div>
                                    {rowData.total ? `${currency}${rowData.total}` : '0.00'}
                                </div>
                            )}
                        />
                        <Column
                            field="startTime"
                            className='receipt_report_table startTime' />
                        <Column
                            field="endTime"
                            className='receipt_report_table endTime' />
                    </DataTable>
                )}
            </div>
        );
    };

    return (
        <Layout2 items={reportNav.items} navTitle={reportNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Table Reports'} />
                <div className='d-flex flex-row px-4 py-2 user-list-nav justify-content-end'>
                    <DateRangeSelection dates={dates} setDates={setDates} />
                </div>
                <div className='px-4 py-2'>
                    {receiptId && (
                        <ViewSingleReceiptReport receiptId={receiptId} setReceiptId={setReceiptId} />
                    )}
                    <div className='report-chart-section d-flex justify-content-between mt-3'>
                        {tableReserve.x.length > 0 && tableReserve.y.length > 0 && (
                            <div className='w-100 text-center me-5'>
                                <CFormLabel>Table Reservation Time</CFormLabel>
                                <BarChart x={tableReserve.x} y={tableReserve.y} yLabel={'Hour'} />
                            </div>
                        )}
                        {paymentChartData.x.length > 0 && paymentChartData.y.length > 0 && (
                            <div className='w-100 text-center self-center'>
                                <CFormLabel>Table Revenue</CFormLabel>
                                <BarChart x={paymentChartData.x} y={paymentChartData.y} yLabel={'Revenue'} />
                            </div>
                        )}
                    </div>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <DataTable
                            className="customerTable thead-dark report-table"
                            totalRecords={totalRecords}
                            lazy
                            paginator
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                if (!e.sortField && e.sortOrder === 0) {
                                    handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                                } else {
                                    handleSorting(e);
                                }
                            }}
                            removableSort
                            rows={rowsPerPage}
                            value={reportList}
                            first={(currentPage - 1) * rowsPerPage}
                            onPage={onPageChange}
                            dataKey="tableId"
                            emptyMessage="No table reports found."
                            expandedRows={expandedProductRows}
                            onRowToggle={(e) => setExpandedProductRows(e.data)}
                            rowExpansionTemplate={rowExpansionTemplate}
                        >
                            <Column
                                expander={allowExpansion}
                                style={{ width: '3em' }} />
                            <Column
                                field="tableId"
                                header="Id"
                                className='receipt_report_table table_id p-0'
                                sortable />
                            <Column
                                field="tableName"
                                header="Name"
                                className='receipt_report_table tableName p-0'
                                sortable />
                            <Column
                                field="floorName"
                                header="Floor Name"
                                className='receipt_report_table floorName p-0'
                                sortable />
                            <Column
                                field="totalHours"
                                header="Total Hours"
                                className='receipt_report_table totalHours p-0'
                                sortable />
                            <Column
                                field="totalRevenue"
                                header="Total Revenue"
                                className='receipt_report_table totalRevenue p-0'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.totalRevenue ? `${currency}${rowData.totalRevenue}` : '0.00'}
                                    </div>
                                )}
                            />
                            <Column
                                field="startDate"
                                header="Start On"
                                className='receipt_report_table startTime p-0'
                                sortable />
                            <Column
                                field="lastDate"
                                header="Paid On"
                                className='receipt_report_table endTime p-0'
                                sortable />
                        </DataTable>
                    )}
                </div>
            </div>
        </Layout2>
    );
}

export default TableReport;
